import { useEffect, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { notifyEditPermissionDenied } from 'utils/editPermissionToast';

const normalizeLimit = (value) => {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "";

  if (/^\d+$/.test(trimmed)) {
    return `${Number(trimmed)}:00`;
  }

  const match = trimmed.match(/^(\d+):(\d{1,2})$/);
  if (!match) return null;

  const minutes = Number(match[1]);
  const seconds = Number(match[2]);
  if (Number.isNaN(minutes) || Number.isNaN(seconds) || seconds > 59) {
    return null;
  }

  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

export default function Limits({ item, canEdit = false, readOnly = false, compact = false }) {
  const url = process.env.REACT_APP_URL;
  const { user: authUser } = useSelector(x => x.auth) || {};
  const [editingField, setEditingField] = useState("");
  const [values, setValues] = useState({
    green: item?.agendaEvent?.green || "",
    amber: item?.agendaEvent?.amber || "",
    red: item?.agendaEvent?.red || "",
  });

  useEffect(() => {
    setValues({
      green: item?.agendaEvent?.green || "",
      amber: item?.agendaEvent?.amber || "",
      red: item?.agendaEvent?.red || "",
    });
    setEditingField("");
  }, [item?.agendaEvent?.green, item?.agendaEvent?.amber, item?.agendaEvent?.red]);

  const saveField = async (field) => {
    if (!authUser?.token || !canEdit) {
      notifyEditPermissionDenied(authUser);
      setEditingField("");
      return;
    }
    if (!item?.agendaEvent?._id) {
      setEditingField("");
      return;
    }

    const normalized = normalizeLimit(values[field]);
    if (normalized === null) {
      toast.error("Use minutes or mm:ss format (example: 5 or 5:30).", { position: "bottom-left" });
      setValues(prev => ({ ...prev, [field]: item?.agendaEvent?.[field] || "" }));
      setEditingField("");
      return;
    }

    if (String(item?.agendaEvent?.[field] || "") === String(normalized || "")) {
      setEditingField("");
      return;
    }

    try {
      const response = await fetch(`${url}/agendaEvent/${item.agendaEvent._id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${authUser.token}`,
        },
        body: JSON.stringify({ [field]: normalized || null }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || "Unable to save timing limit.");
      }

      if (item?.agendaEvent) {
        item.agendaEvent[field] = normalized || "";
      }
      setValues(prev => ({ ...prev, [field]: normalized || "" }));
    } catch (err) {
      toast.error(err.message || "Failed to save timing limit.", { position: "bottom-left" });
      setValues(prev => ({ ...prev, [field]: item?.agendaEvent?.[field] || "" }));
    } finally {
      setEditingField("");
    }
  };

  const renderLimitCell = (field, backgroundColor, textColor) => {
    const isEditing = editingField === field;
    return (
      <Col
        style={{
          backgroundColor,
          color: textColor,
          textAlign: "center",
          cursor: canEdit ? "pointer" : "default",
          minHeight: "28px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        onClick={() => {
          if (readOnly) return;
          if (!canEdit || !authUser?.token) {
            notifyEditPermissionDenied(authUser);
            return;
          }
          setEditingField(field);
        }}
      >
        {isEditing ? (
          <Form.Control
            size="sm"
            value={values[field]}
            onChange={(e) => setValues(prev => ({ ...prev, [field]: e.target.value }))}
            onBlur={() => saveField(field)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.currentTarget.blur();
              }
              if (e.key === "Escape") {
                setValues(prev => ({ ...prev, [field]: item?.agendaEvent?.[field] || "" }));
                setEditingField("");
              }
            }}
            autoFocus
            style={{ maxWidth: "90px", textAlign: "center" }}
            placeholder="m:ss"
          />
        ) : (
          values[field] || "--"
        )}
      </Col>
    );
  };

  return (
    <>
      <Row className={`limits${compact ? ' activity-timing-limits' : ''}`} aria-label={readOnly ? 'Timing limits' : undefined}>
        {renderLimitCell("green", "#146c43", "#ffffff")}
        {renderLimitCell("amber", "#ffc107", "#000000")}
        {renderLimitCell("red", "#dc3545", "#ffffff")}
      </Row>
    </>
  );
}
